import { useMemo, useState } from 'react'

import { Download } from 'lucide-react'

import { useConfirmedHotelTodayReservations } from '@/components/ReservationContext'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { CardHeader, CardTitle } from '@/shared/ui/card'
import { Input } from '@/shared/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import type { HotelReservation, Pet } from '@monorepo/functions/src/types/reservations'
import { MedicationService, SpecialCareService, SpecialFoodService } from '@monorepo/functions/src/types/services'

type DailyNeed = {
    roomNumber: string
    petName: string
    medication?: MedicationService
    specialCare?: SpecialCareService
    feeding?: SpecialFoodService
    observations: string
}

const mapReservationsToDailyNeeds = (reservations: HotelReservation[]): DailyNeed[] => {
    return reservations.flatMap(reservation => {
        return reservation.pets.map((pet: Pet, petIndex: number) => {
            const medicationService = reservation.additionalServices.find(
                service => service.type === 'medication' && service.petIndex === petIndex,
            ) as MedicationService | undefined
            const specialCareService = reservation.additionalServices.find(
                service => service.type === 'special_care' && service.petIndex === petIndex,
            ) as SpecialCareService | undefined

            const feedingService = reservation.additionalServices.find(
                service => service.type === 'special_food' && service.petIndex === petIndex,
            ) as SpecialFoodService | undefined

            return {
                roomNumber: pet.roomNumber ?? 'Sin asignar',
                petName: pet.name,
                medication: medicationService,
                specialCare: specialCareService,
                feeding: feedingService,
                observations: pet.observations ?? '-',
            }
        })
    })
}

export function DailyNeedsList() {
    const [searchTerm, setSearchTerm] = useState('')

    const { reservations: activeReservations } = useConfirmedHotelTodayReservations()

    const dailyNeeds = useMemo(() => mapReservationsToDailyNeeds(activeReservations), [activeReservations])

    const filteredNeeds = dailyNeeds.filter(
        need =>
            need.roomNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            need.petName.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    const handleExport = () => {
        // Crear el contenido CSV
        const headers = ['Habitación', 'Mascota', 'Medicación', 'Curas', 'Alimentación', 'Alergias', 'Advertencias']
        const rows = filteredNeeds.map(need => [
            need.roomNumber,
            need.petName,
            need.medication,
            need.specialCare,
            need.feeding,
            need.observations,
        ])

        const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n')

        // Crear y descargar el archivo
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `necesidades_diarias_${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <div className='space-y-6'>
            <CardHeader className='px-0 pt-0'>
                <CardTitle className='text-2xl font-bold text-gray-900'>Indicaciones</CardTitle>
            </CardHeader>

            <div className='flex items-center justify-between gap-4'>
                <div className='relative flex-1'>
                    <Input
                        placeholder='Buscar por habitación o nombre de mascota'
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className='max-w-sm pl-4'
                    />
                </div>
                <Button variant='outline' size='sm' onClick={handleExport} className='whitespace-nowrap'>
                    <Download className='mr-2 h-4 w-4' />
                    Exportar
                </Button>
            </div>

            <div className='rounded-lg border bg-white'>
                <Table>
                    <TableHeader>
                        <TableRow className='bg-gray-50'>
                            <TableHead className='font-semibold text-gray-900'>Habitación</TableHead>
                            <TableHead className='font-semibold text-gray-900'>Mascota</TableHead>
                            <TableHead className='font-semibold text-gray-900'>Medicación</TableHead>
                            <TableHead className='font-semibold text-gray-900'>Cuidados especiales</TableHead>
                            <TableHead className='font-semibold text-gray-900'>Alimentación</TableHead>
                            <TableHead className='font-semibold text-gray-900'>Observaciones</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredNeeds.map((need, index) => (
                            <TableRow key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <TableCell className='font-medium'>{need.roomNumber}</TableCell>
                                <TableCell>{need.petName}</TableCell>
                                <TableCell className={need.medication ? 'text-blue-700' : 'text-gray-500'}>
                                    {need.medication ? (
                                        <>
                                            <Badge
                                                variant='outline'
                                                className={
                                                    need.medication.frequency === 'multiple'
                                                        ? 'bg-blue-500 text-gray-100'
                                                        : 'bg-gray-200 text-gray-800'
                                                }
                                            >
                                                {need.medication.frequency === 'multiple' ? 'Múltiples' : 'Única'}
                                            </Badge>
                                            <span> {need.medication.comment}</span>
                                        </>
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                                <TableCell className={need.specialCare ? 'text-purple-700' : 'text-gray-500'}>
                                    {need.specialCare?.comment ?? '-'}
                                </TableCell>
                                <TableCell className='text-green-700'>
                                    {need.feeding ? (
                                        <>{need.feeding.foodType === 'refrigerated' ? 'Refrigerada' : 'Congelada'}</>
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                                <TableCell className='text-orange-700'>{need.observations}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
